import type { ScheduledFrequency } from '@/data/scheduledReminders';
import clsx from 'clsx';
import { useState } from 'react';

interface FrequencyEditorProps {
  frequency: ScheduledFrequency;
  onChange: (f: ScheduledFrequency) => void;
}

function parseMinutes(input: string) {
  return input
    .split(',')
    .map(s => parseInt(s.trim(), 10))
    .filter(n => !isNaN(n) && n >= 0 && n < 60);
}

function parseTimes(input: string) {
  return input
    .split(',')
    .map(s => {
      const parts = s.trim().split(':');
      const h = parseInt(parts[0] ?? '0', 10);
      const m = parseInt(parts[1] ?? '0', 10);
      if (isNaN(h) || h < 0 || h > 23 || isNaN(m) || m < 0 || m > 59) {
        return null;
      }
      return { hour: h, minute: m };
    })
    .filter((t): t is { hour: number; minute: number } => t !== null);
}

function TextTimeInput({
  label,
  placeholder,
  defaultValue,
  onParse,
}: {
  label: string;
  placeholder: string;
  defaultValue: string;
  onParse: (times: { hour: number; minute: number }[]) => void;
}) {
  const [input, setInput] = useState(defaultValue);

  const handleBlur = () => {
    const parsed = parseTimes(input);
    onParse(parsed.length > 0 ? parsed : [{ hour: 0, minute: 0 }]);
    setInput(
      (parsed.length > 0 ? parsed : [{ hour: 0, minute: 0 }])
        .map(t => `${t.hour}:${t.minute.toString().padStart(2, '0')}`)
        .join(', '),
    );
  };

  return (
    <div className="space-y-2">
      <label className="block">{label}</label>
      <input
        type="text"
        value={input}
        onChange={e => setInput(e.target.value)}
        onBlur={handleBlur}
        className="w-full rounded border border-gray-300 px-3 py-2"
        placeholder={placeholder}
      />
      <small className="text-gray-500">
        comma-separated list, 24-hour format
      </small>
    </div>
  );
}

function TextMinutesInput({
  defaultValue,
  onParse,
}: {
  defaultValue: string;
  onParse: (minutes: number[]) => void;
}) {
  const [input, setInput] = useState(defaultValue);

  const handleBlur = () => {
    const parsed = parseMinutes(input);
    onParse(parsed.length > 0 ? parsed : [0]);
    setInput((parsed.length > 0 ? parsed : [0]).join(', '));
  };

  return (
    <div className="space-y-2">
      <label className="block">Minutes in hour</label>
      <input
        type="text"
        value={input}
        onChange={e => setInput(e.target.value)}
        onBlur={handleBlur}
        className="w-full rounded border border-gray-300 px-3 py-2"
        placeholder="e.g. 0, 15, 30, 45"
      />
      <small className="text-gray-500">
        comma-separated list (0-59), e.g. 55 for 5 min before hour
      </small>
    </div>
  );
}

function IntervalEditor({
  frequency,
  onChange,
}: {
  frequency: Extract<ScheduledFrequency, { kind: 'interval' }>;
  onChange: (f: Extract<ScheduledFrequency, { kind: 'interval' }>) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <label className="w-20">Every</label>
        <div className="flex gap-2">
          <div>
            <input
              type="number"
              min="0"
              value={frequency.every.minutes ?? ''}
              onChange={e => {
                const val = e.target.value;
                onChange({
                  ...frequency,
                  every: {
                    ...frequency.every,
                    minutes:
                      val === ''
                        ? undefined
                        : Math.max(0, parseInt(val, 10) || 0),
                  },
                });
              }}
              className="w-16 rounded border border-gray-300 px-2 py-1"
              placeholder="min"
            />
            <small className="block text-gray-500">minutes</small>
          </div>
          <div>
            <input
              type="number"
              min="0"
              value={frequency.every.hours ?? ''}
              onChange={e => {
                const val = e.target.value;
                onChange({
                  ...frequency,
                  every: {
                    ...frequency.every,
                    hours:
                      val === ''
                        ? undefined
                        : Math.max(0, parseInt(val, 10) || 0),
                  },
                });
              }}
              className="w-16 rounded border border-gray-300 px-2 py-1"
              placeholder="hrs"
            />
            <small className="block text-gray-500">hours</small>
          </div>
        </div>
      </div>
    </div>
  );
}

function HourlyEditor({
  frequency,
  onChange,
}: {
  frequency: Extract<ScheduledFrequency, { kind: 'hourly' }>;
  onChange: (f: Extract<ScheduledFrequency, { kind: 'hourly' }>) => void;
}) {
  return (
    <TextMinutesInput
      defaultValue={(frequency.minutes ?? [0]).join(', ')}
      onParse={minutes => onChange({ ...frequency, minutes })}
    />
  );
}

function DailyEditor({
  frequency,
  onChange,
}: {
  frequency: Extract<ScheduledFrequency, { kind: 'daily' }>;
  onChange: (f: Extract<ScheduledFrequency, { kind: 'daily' }>) => void;
}) {
  return (
    <TextTimeInput
      label="Times (hour:minute)"
      placeholder="e.g. 09:00, 12:00, 17:30"
      defaultValue={(frequency.times ?? [])
        .map(t => `${t.hour}:${t.minute.toString().padStart(2, '0')}`)
        .join(', ')}
      onParse={times => onChange({ ...frequency, times })}
    />
  );
}

function WeeklyEditor({
  frequency,
  onChange,
}: {
  frequency: Extract<ScheduledFrequency, { kind: 'weekly' }>;
  onChange: (f: Extract<ScheduledFrequency, { kind: 'weekly' }>) => void;
}) {
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="space-y-4">
      <div>
        <label className="mb-2 block">Days</label>
        <div className="flex gap-1">
          {dayNames.map((day, i) => (
            <button
              key={i}
              onClick={() => {
                const days = frequency.days ?? [];
                const newDays = days.includes(i)
                  ? days.filter(d => d !== i)
                  : [...days, i];
                onChange({ ...frequency, days: newDays });
              }}
              className={clsx(
                'rounded border px-3 py-1',
                (frequency.days ?? []).includes(i)
                  ? 'border-blue-600 bg-blue-500 text-white'
                  : 'border-gray-300 bg-gray-100 text-gray-900',
              )}
            >
              {day}
            </button>
          ))}
        </div>
      </div>
      <TextTimeInput
        label="Times (hour:minute)"
        placeholder="e.g. 09:00, 17:00"
        defaultValue={(frequency.times ?? [])
          .map(t => `${t.hour}:${t.minute.toString().padStart(2, '0')}`)
          .join(', ')}
        onParse={times => onChange({ ...frequency, times })}
      />
    </div>
  );
}

export function FrequencyEditor({ frequency, onChange }: FrequencyEditorProps) {
  switch (frequency.kind) {
    case 'interval':
      return <IntervalEditor frequency={frequency} onChange={onChange} />;
    case 'hourly':
      return <HourlyEditor frequency={frequency} onChange={onChange} />;
    case 'daily':
      return <DailyEditor frequency={frequency} onChange={onChange} />;
    case 'weekly':
      return <WeeklyEditor frequency={frequency} onChange={onChange} />;
  }
}
